import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore, selectIsAuthenticated } from '@/stores/authStore'

/** Route of the terms acceptance gate; shared with AuthCallbackPage. */
export const TERMS_GATE_PATH = '/akceptacja-regulaminu'

interface RequireAuthProps {
    children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
    const { t } = useTranslation()
    const isInitialized = useAuthStore((s) => s.isInitialized)
    const isAuthenticated = useAuthStore(selectIsAuthenticated)
    const termsAcceptanceRequired = useAuthStore((s) => s.user?.terms_acceptance_required === true)
    const location = useLocation()

    if (!isInitialized) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <svg
                        className="animate-spin h-8 w-8 text-pink-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                    <p className="text-slate-500 text-xs uppercase tracking-widest font-mono">
                        {t('common.authenticating')}
                    </p>
                </div>
            </div>
        )
    }

    if (!isAuthenticated) {
        return (
            <Navigate
                to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`}
                replace
            />
        )
    }

    // The gate page itself is exempt, otherwise accepting would be unreachable.
    if (termsAcceptanceRequired && location.pathname !== TERMS_GATE_PATH) {
        const returnUrl = `${location.pathname}${location.search}`
        return (
            <Navigate
                to={`${TERMS_GATE_PATH}?returnUrl=${encodeURIComponent(returnUrl)}`}
                replace
            />
        )
    }

    return <>{children}</>
}
